import React, { useCallback, useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";

import { apiSlice } from "fond/api/apiSlice";
import { useDeleteLayerMutation } from "fond/api/layersSlice";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { refreshTileSource } from "fond/map/redux";
import { polygonDrawingConfig } from "fond/map/Toolbar";
import LayerDraw from "fond/project/panels/InputPanel/LayerDraw";
import LayerPanel from "fond/project/panels/InputPanel/LayerPanel";
import LayerUploadWrapper from "fond/project/panels/InputPanel/LayerUploadWrapper";
import { isImportInProgress, MuPowerEditableLayerIds, MuPowerInputLayerIds, MuPowerSolverLayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { isAnyPolygon } from "fond/types/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";

import { muPowerInputLayers } from "./InputPanel/layers";
import MuPowerBoundaryImport from "./InputPanel/MuPowerBoundaryImport";
import MuPowerBoundaryPanel from "./InputPanel/MuPowerBoundaryPanel";
import DataPrepPanel from "./DataPrepPanel";
import { InputPanel } from "./InputPanel";
import ModificationsPanel from "./ModificationsPanel";
import MuPowerBlockersDraw from "./MuPowerBlockersDraw";
import MuPowerDraw from "./MuPowerDraw";
import MuPowerFootprintDraw from "./MuPowerFootprintDraw";
import MuPowerLeadInDraw from "./MuPowerLeadInDraw";
import MuPowerMultiLayerUpload from "./MuPowerMultiLayerUpload";
import MuPowerSourceHubsDraw from "./MuPowerSourceHubsDraw";
import PowerSolverPanel from "./PowerSolverPanel";

interface PanelProps {
  project: Project;
  readOnly: boolean;
}

const Panel: React.FC<PanelProps> = ({ project, readOnly }: PanelProps) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const inputLayers = useGetImportLayers(versionId, Object.values(MuPowerInputLayerIds));
  const solverLayers = useGetImportLayers(versionId, Object.values(MuPowerSolverLayerIds));
  const editableLayers = useGetImportLayers(versionId, Object.values(MuPowerEditableLayerIds));

  const hasInProgressLayers = useMemo(() => {
    return Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));
  }, [inputLayers]);

  const allRequiredLayersUploaded = useMemo(() => {
    return muPowerInputLayers.filter(({ isRequired }) => isRequired).every(({ key }) => !!inputLayers.get(key)?.layer);
  }, [inputLayers]);

  const { workflowState: dataPrepWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.MU_POWER.DATA_PREP });
  const isDataPrepInProgress = dataPrepWorkflow === "STARTING" || dataPrepWorkflow === "IN_PROGRESS";

  const { workflowState: powerSolverWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.MU_POWER.POWER_SOLVER });
  const isPowerSolverInProgress = powerSolverWorkflow === "STARTING" || powerSolverWorkflow === "IN_PROGRESS";

  const dataPrepComplete = dataPrepWorkflow === "COMPLETE";

  const [deleteLayer] = useDeleteLayerMutation();
  const deleteLayersByKey = useCallback(
    async (layerKeys: string[]) => {
      const ids = layerKeys.map((key) => solverLayers.get(key)?.layer?.ID).filter((id): id is string => !!id);
      if (ids.length === 0) return;
      await Promise.all(ids.map((id) => deleteLayer(id).unwrap()));
      dispatch(refreshTileSource("project-layers"));
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "FeatureTotals", id: versionId },
          { type: "Layers", id: versionId },
          { type: "VersionRootConfiguration", id: versionId },
          { type: "ImportStatus", id: versionId },
        ])
      );
    },
    [deleteLayer, solverLayers, dispatch, versionId]
  );

  const muPowerStackedNavigationPanels = useMemo<Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }>>(() => {
    const layerPanels = muPowerInputLayers.flatMap(({ key, displayName, navigationKey, geometryType }) => {
      const uploadScreen = {
        id: `${navigationKey}-upload`,
        name: "Upload",
        component: (
          <LayerUploadWrapper
            layerId={key}
            displayName={displayName}
            title={`Upload ${displayName.toLowerCase()}`}
            geometryType={geometryType}
            hasExistingData={!!inputLayers.get(key)?.layer}
          />
        ),
        useDefaultHeader: false,
      };

      if (key === MuPowerInputLayerIds.boundary) {
        return [
          {
            id: key,
            name: displayName,
            component: <MuPowerBoundaryPanel hasBoundary={!!inputLayers.get(key)?.layer} />,
            useDefaultHeader: false,
          },
          {
            id: `${navigationKey}-draw`,
            name: "Draw Boundary",
            component: (
              <LayerDraw
                versionId={versionId}
                layerId={key}
                displayName={displayName}
                drawingConfig={polygonDrawingConfig}
                filterFeatures={isAnyPolygon}
                combineToMultiPolygon
              />
            ),
            useDefaultHeader: false,
          },
          uploadScreen,
          {
            id: `${navigationKey}-import`,
            name: "Import",
            component: <MuPowerBoundaryImport />,
            useDefaultHeader: false,
          },
        ];
      }

      return [
        {
          id: key,
          name: displayName,
          component: <LayerPanel title={displayName} layerKey={navigationKey} actions={["upload"]} />,
          useDefaultHeader: false,
        },
        uploadScreen,
      ];
    });

    const sourceHubsKey = MuPowerSolverLayerIds.sourceHubs;
    const sourceHubsHasData = !!solverLayers.get(sourceHubsKey)?.layer;

    return [
      ...layerPanels,
      {
        id: "modification-muPowerUnderground",
        name: "Underground",
        component: (
          <LayerPanel
            title="Underground"
            layerKey="mu-modification-underground"
            actions={["draw", "upload"]}
            drawTitle="Edit Underground"
            drawSubtitle="Edit or draw underground on the map"
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-modification-underground-draw",
        name: "Edit Underground",
        component: <MuPowerDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-modification-underground-upload",
        name: "Upload Underground",
        component: (
          <MuPowerMultiLayerUpload
            title="Upload Underground"
            sections={[
              { layerId: MuPowerEditableLayerIds.ugPath, displayName: "UG Path", geometryType: "LineString" },
              { layerId: MuPowerEditableLayerIds.ugPoints, displayName: "UG Points", geometryType: "Point" },
            ]}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "modification-muPowerLeadins",
        name: "Leadins",
        component: (
          <LayerPanel
            title="Leadins"
            layerKey="mu-modification-leadins"
            actions={["draw", "upload"]}
            drawTitle="Edit Leadins"
            drawSubtitle="Edit or draw leadins on the map"
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-modification-leadins-draw",
        name: "Edit Leadins",
        component: <MuPowerLeadInDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-modification-leadins-upload",
        name: "Upload Leadins",
        component: (
          <LayerUploadWrapper
            layerId={MuPowerEditableLayerIds.leadins}
            displayName="Leadins"
            title="Upload leadins"
            geometryType="LineString"
            hasExistingData={!!editableLayers.get(MuPowerEditableLayerIds.leadins)?.layer}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: sourceHubsKey,
        name: "Source Hubs",
        component: (
          <LayerPanel
            title="Source Hubs"
            layerKey="mu-source-hubs"
            actions={["draw", "upload", "delete"]}
            drawTitle="Edit Source Hubs"
            drawSubtitle="Edit or draw source hubs on the map"
            canDelete={sourceHubsHasData}
            onDelete={() => deleteLayersByKey([sourceHubsKey])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-source-hubs-upload",
        name: "Upload",
        component: (
          <LayerUploadWrapper
            layerId={sourceHubsKey}
            displayName="Source Hubs"
            title="Upload source hubs"
            geometryType="Point"
            hasExistingData={sourceHubsHasData}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-source-hubs-draw",
        name: "Source Hubs",
        component: <MuPowerSourceHubsDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-blockers",
        name: "Blockers",
        component: (
          <LayerPanel
            title="Blockers"
            layerKey="mu-blockers"
            actions={["draw", "upload", "delete"]}
            drawTitle="Edit Blockers"
            drawSubtitle="Edit or draw blockers on the map"
            canDelete={
              !!solverLayers.get(MuPowerSolverLayerIds.primaryBlockers)?.layer || !!solverLayers.get(MuPowerSolverLayerIds.secondaryBlockers)?.layer
            }
            onDelete={() => deleteLayersByKey([MuPowerSolverLayerIds.primaryBlockers, MuPowerSolverLayerIds.secondaryBlockers])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-blockers-draw",
        name: "Edit Blockers",
        component: <MuPowerBlockersDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-blockers-upload",
        name: "Upload Blockers",
        component: (
          <MuPowerMultiLayerUpload
            title="Upload Blockers"
            sections={[
              { layerId: MuPowerSolverLayerIds.primaryBlockers, displayName: "Primary Blockers", geometryType: "LineString" },
              { layerId: MuPowerSolverLayerIds.secondaryBlockers, displayName: "Secondary Blockers", geometryType: "LineString" },
            ]}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-footprint",
        name: "Footprint",
        component: (
          <LayerPanel
            title="Footprint"
            layerKey="mu-footprint"
            actions={["draw", "upload", "delete"]}
            drawTitle="Edit Footprint"
            drawSubtitle="Edit or draw footprint on the map"
            canDelete={
              !!solverLayers.get(MuPowerSolverLayerIds.primaryFootprint)?.layer || !!solverLayers.get(MuPowerSolverLayerIds.secondaryFootprint)?.layer
            }
            onDelete={() => deleteLayersByKey([MuPowerSolverLayerIds.primaryFootprint, MuPowerSolverLayerIds.secondaryFootprint])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-footprint-draw",
        name: "Edit Footprint",
        component: <MuPowerFootprintDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-footprint-upload",
        name: "Upload Footprint",
        component: (
          <MuPowerMultiLayerUpload
            title="Upload Footprint"
            sections={[
              { layerId: MuPowerSolverLayerIds.primaryFootprint, displayName: "Primary Footprint", geometryType: "LineString" },
              { layerId: MuPowerSolverLayerIds.secondaryFootprint, displayName: "Secondary Footprint", geometryType: "LineString" },
            ]}
          />
        ),
        useDefaultHeader: false,
      },
    ];
  }, [inputLayers, solverLayers, editableLayers, deleteLayersByKey, versionId]);

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Input data",
        id: "input",
        content: <InputPanel isReadOnly={readOnly} inputLayers={inputLayers} />,
        isComplete: allRequiredLayersUploaded && !hasInProgressLayers,
        isLoading: hasInProgressLayers,
      },
      {
        header: "Data Prep",
        id: "dataPrep",
        content: <DataPrepPanel isReadOnly={readOnly} canRun={allRequiredLayersUploaded} />,
        isComplete: dataPrepComplete,
        isLoading: isDataPrepInProgress,
      },
      {
        header: "Modifications",
        id: "modifications",
        content: <ModificationsPanel />,
        isComplete: !!solverLayers.get(MuPowerSolverLayerIds.sourceHubs)?.layer,
        isLoading: false,
      },
      {
        header: "Design",
        id: "powerSolver",
        content: <PowerSolverPanel isReadOnly={readOnly} canRun={dataPrepComplete} solverLayers={solverLayers} project={project} />,
        isComplete: powerSolverWorkflow === "COMPLETE",
        isLoading: isPowerSolverInProgress,
      },
    ],
    [
      allRequiredLayersUploaded,
      dataPrepComplete,
      hasInProgressLayers,
      inputLayers,
      isDataPrepInProgress,
      isPowerSolverInProgress,
      powerSolverWorkflow,
      project,
      readOnly,
      solverLayers,
    ]
  );

  if (!project) return null;

  return (
    <Box data-testid="mu-power-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? (
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <StackedNavigation rootComponent={<AccordionStepper panes={accordionPanes} id={project.ID} />} screens={muPowerStackedNavigationPanels} />
      )}
    </Box>
  );
};

export default Panel;
